import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { CARD_TYPES, CARD_ACTION_TYPES } from '../../../constants/paymentCardConstans';
import CardActionsModal from '../CardActionsModal/CardActionsModal';

import visaCardLogo from '../../../img/visa.svg';
import arcaCardLogo from '../../../img/arca.svg';
import mirCardLogo from '../../../img/mir.svg';
import mastercardCardLogo from '../../../img/mastercard.svg';
import amexCardLogo from '../../../img/amex.svg';
import cardTypeLogo from '../../../img/cardType.svg';
import warningIcon from '../../../img/warning.svg';

import styles from './PaymentCard.module.scss';

const cardTypeLogos = {
  visa: visaCardLogo,
  arca: arcaCardLogo,
  mir: mirCardLogo,
  mastercard: mastercardCardLogo,
  amex: amexCardLogo,
};

const PaymentCard = ({ card, removeCard, makeDefault }) => {
  const [openModal, setOpenModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const { t } = useTranslation();

  const { cardPan, expDate, isDefault, isExpired } = card;

  const formattedDate = `${expDate.slice(-2)}/${expDate.slice(-4, -2)}`;
  const maskedCardPan = `${'*'.repeat(cardPan.length - 4)}${cardPan.slice(-4)}`;

  const cardType = useMemo(() => {
    const firstFourDigits = cardPan.substring(0, 4);
    return CARD_TYPES.find((type) => new RegExp(type.regex).test(firstFourDigits)) || null;
  }, [cardPan]);

  const handleMakeDefault = () => {
    setOpenModal(true);
    setActionType(CARD_ACTION_TYPES.MAKE_DEFAULT);
  };

  const handleDeleteCard = () => {
    setOpenModal(true);
    setActionType(CARD_ACTION_TYPES.DELETE);
  };

  return (
    <>
      <div className={styles.cardWrapper}>
        <div className={cn(styles.cardContainer, {
          [styles.expiredCardContainer]: isExpired,
        })}
        >
          <div className={styles.cardType}>
            <img
              className={styles.nonVisible}
              alt={cardType.type}
              src={cardTypeLogos[cardType.type] || cardTypeLogo}
            />
          </div>
          <div className={styles.cardNumbers}>
            {maskedCardPan}
          </div>
          <div className={styles.cardBottom}>
            {isDefault ? <div className={styles.defaultCardLabel}>{t('myCards.default')}</div> : <div />}
            <div className={styles.cardDateSection}>
              <div className={styles.cardLabel}>{t('myCards.cardValidThru')}</div>
              <div className={styles.cardDate}>{formattedDate}</div>
            </div>
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          {isExpired ? (
            <div className={styles.expired}>
              <img className={styles.warning} src={warningIcon} alt="warning" />
              {t('myCards.expired')}
            </div>
          ) : (
            <>
              {isDefault ? <div /> : <button type="button" className={styles.makeDefaultButton} onClick={handleMakeDefault}>{t('myCards.makeDefault')}</button>}
            </>
          )}
          <button type="button" disabled={isDefault} className={styles.deleteCardButton} onClick={handleDeleteCard}><span>{t('myCards.deleteCard')}</span></button>
        </div>
      </div>
      <CardActionsModal
        cardHolderId={card.cardHolderID}
        openModal={openModal}
        setOpenModal={setOpenModal}
        actionType={actionType}
        makeDefault={makeDefault}
        removeCard={removeCard}
      />
    </>
  );
};

PaymentCard.propTypes = {
  card: PropTypes.shape({
    cardPan: PropTypes.string.isRequired,
    expDate: PropTypes.string.isRequired,
    isDefault: PropTypes.bool.isRequired,
    isExpired: PropTypes.bool.isRequired,
    cardHolderID: PropTypes.string.isRequired,
  }).isRequired,
  removeCard: PropTypes.func.isRequired,
  makeDefault: PropTypes.func.isRequired,
};

export default PaymentCard;
